import { format } from 'date-fns'

import { Class, HistoricFollowerYear } from './ExploreTable/gradYears'
import { APIFilters, UIFilters } from './types'

type Entries<T> = {
  [K in keyof T]-?: [K, T[K]]
}[keyof T][]
const entries = <T extends object>(obj: T): Entries<T> => Object.entries(obj) as Entries<T>

export const createURLSeachParamsFromAPIFilters = (filters: APIFilters): URLSearchParams => {
  const urlParams = new URLSearchParams()
  entries(filters).map((entries) => {
    const [k, v] = entries
    if (v === undefined) {
      return
    }
    if (Array.isArray(v)) {
      v.forEach((item) => {
        urlParams.append(k, String(item))
      })
    } else {
      urlParams.set(k, v.toString())
    }
  })
  return urlParams
}

export type APIFilterContext = {
  handraiserClasses?: Class[]
  followerClasses?: Class[]
  historicFollowerYears?: HistoricFollowerYear[]
}

export const createAPIFiltersFromUIFilters = (filters: UIFilters, context: APIFilterContext = {}): APIFilters => {
  const apiFilters: APIFilters = {}
  entries(apiFilterExtractor).map(([k, fn]) => {
    const value = fn(filters, context)
    // @ts-expect-error the apiFilterExtractor is typed as a mapped APIFilters, but I can't get typescript to understand that
    apiFilters[k] = value
  })
  return apiFilters
}

type APIFilterConverter = {
  [TKey in keyof APIFilters]-?: (f: UIFilters, ctx: APIFilterContext) => APIFilters[TKey]
}

const apiFilterExtractor: APIFilterConverter = {
  schoolType: (f) => f.schoolType,
  religiousAffiliation: (f) => f.religiousAffiliation,
  virtual: (f) => f.virtual,
  collegeFair: (f) => f.collegeFair,
  isActive: (f) => f.isActive,
  title1Program: (f) => f.title1Program,
  isTitle1Eligible: (f) => f.isTitle1Eligible,
  endDate: (f) => formattedDate(f.visitRange?.endDate),
  startDate: (f) => formattedDate(f.visitRange?.startDate),
  minSize: (f) => f.classSize?.min,
  maxSize: (f) => f.classSize?.max,
  pctFreeReducedLunchMin: (f) => f.pctFreeReducedLunch?.min,
  pctFreeReducedLunchMax: (f) => f.pctFreeReducedLunch?.max,
  pctFirstGenerationStudentsMin: (f) => f.pctFirstGenerationStudents?.min,
  pctFirstGenerationStudentsMax: (f) => f.pctFirstGenerationStudents?.max,
  pctAttendFourYearCollegeStudentsMin: (f) => f.pctAttendFourYearCollegeStudents?.min,
  pctAttendFourYearCollegeStudentsMax: (f) => f.pctAttendFourYearCollegeStudents?.max,
  pctAttendTwoYearCollegeStudentsMin: (f) => f.pctAttendTwoYearCollegeStudents?.min,
  pctAttendTwoYearCollegeStudentsMax: (f) => f.pctAttendTwoYearCollegeStudents?.max,
  pctWhiteCaucasianStudentsMin: (f) => f.pctWhiteCaucasianStudents?.min,
  pctWhiteCaucasianStudentsMax: (f) => f.pctWhiteCaucasianStudents?.max,
  pctBlackAfricanAmericanStudentsMin: (f) => f.pctBlackAfricanAmericanStudents?.min,
  pctBlackAfricanAmericanStudentsMax: (f) => f.pctBlackAfricanAmericanStudents?.max,
  pctHispanicLatinoStudentsMin: (f) => f.pctHispanicLatinoStudents?.min,
  pctHispanicLatinoStudentsMax: (f) => f.pctHispanicLatinoStudents?.max,
  pctAsianStudentsMin: (f) => f.pctAsianStudents?.min,
  pctAsianStudentsMax: (f) => f.pctAsianStudents?.max,
  pctNativeAmericanStudentsMin: (f) => f.pctNativeAmericanStudents?.min,
  pctNativeAmericanStudentsMax: (f) => f.pctNativeAmericanStudents?.max,
  pctOtherUndisclosedStudentsMin: (f) => f.pctOtherUndisclosedStudents?.min,
  pctOtherUndisclosedStudentsMax: (f) => f.pctOtherUndisclosedStudents?.max,
  locations: (f) => f.locations,
  text: (f) => f.text,
  locale: (f) => f.locale,
  geomarkets: (f) => f.geomarkets,
  cities: (f) => f.cities,
  zipCodes: (f) => f.zipCodes,
  interestedHandraisersMin: (f) => f.interestedHandraisers?.min,
  interestedHandraisersMax: (f) => f.interestedHandraisers?.max,
  interestedHandraisersClasses: (_f, ctx) => (ctx.handraiserClasses?.length ? ctx.handraiserClasses : undefined),
  interestedFollowersMin: (f) => f.interestedFollowers?.min,
  interestedFollowersMax: (f) => f.interestedFollowers?.max,
  interestedFollowersClasses: (_f, ctx) => (ctx.followerClasses?.length ? ctx.followerClasses : undefined),
  historicFollowersMin: (f) => f.historicFollowers?.min,
  historicFollowersMax: (f) => f.historicFollowers?.max,
  historicFollowersYears: (_f, ctx) => (ctx.historicFollowerYears?.length ? ctx.historicFollowerYears : undefined),
  hasCounselorSuggestions: (f) => f.hasCounselorSuggestions,
  isTarget: (f) => f.isTarget,
}

const formattedDate = (d: Date | undefined) => (d ? format(d, 'yyyy-MM-dd') : undefined)
